import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DescriptionRounded from '@mui/icons-material/DescriptionRounded';
import RouteRounded from '@mui/icons-material/RouteRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import SearchOffRounded from '@mui/icons-material/SearchOffRounded';
import { useTranslation } from 'react-i18next';
import { resolveI18n } from '@fms/contracts';
import { useMyApplications, useServices } from '@fms/api-client';

/**
 * Statuses that still count as "you have one of these open". Mirrors the
 * server's own list — the API refuses a second draft while one is live, so the
 * card has to offer tracking instead of an Apply button that only leads to an
 * error page.
 */
const LIVE_STATUSES = new Set(['SUBMITTED', 'IN_PROGRESS', 'PENDING_CITIZEN', 'PENDING_PAYMENT']);

const TILE_TONES = [
  'var(--fms-color-primary)',
  'var(--fms-color-accent)',
  'var(--fms-color-success)',
  'var(--fms-color-warning-vivid)',
  '#6366f1',
  '#0ea5e9',
];

function tone(code: string): string {
  let hash = 0;
  for (const ch of code) hash = (hash * 31 + ch.charCodeAt(0)) | 0;
  return TILE_TONES[Math.abs(hash) % TILE_TONES.length]!;
}

export function Catalog() {
  const { t, i18n } = useTranslation();
  const { data, isLoading, isError } = useServices();
  const mine = useMyApplications();
  const [query, setQuery] = useState('');
  const [department, setDepartment] = useState('');
  const [kind, setKind] = useState<'' | 'FORM' | 'JOURNEY'>('');

  const services = useMemo(() => data?.services ?? [], [data]);
  /** serviceCode → the application already open for it. */
  const liveByService = useMemo(() => {
    const map = new Map<string, { id: string; arn: string }>();
    for (const row of mine.data?.rows ?? []) {
      if (LIVE_STATUSES.has(row.status) && !map.has(row.serviceCode)) {
        map.set(row.serviceCode, { id: row.id, arn: row.arn });
      }
    }
    return map;
  }, [mine.data]);
  const departments = useMemo(
    () => [...new Set(services.map((s) => s.departmentCode))].sort(),
    [services],
  );

  const filtered = services.filter((service) => {
    const name = (resolveI18n(service.nameI18n, i18n.language) || service.name).toLowerCase();
    const summary = (
      resolveI18n(service.summaryI18n, i18n.language) ||
      service.summary ||
      ''
    ).toLowerCase();
    const q = query.trim().toLowerCase();
    return (
      (!q || name.includes(q) || summary.includes(q)) &&
      (!department || service.departmentCode === department) &&
      (!kind || service.kind === kind)
    );
  });

  const clearFilters = () => {
    setQuery('');
    setDepartment('');
    setKind('');
  };

  return (
    <Stack spacing={2.5}>
      <Box>
        <Typography variant="h1">{t('catalog.title')}</Typography>
        <Typography color="text.secondary" sx={{ mt: 0.5 }}>
          {t('catalog.subtitle')}
        </Typography>
      </Box>

      {/* One filter bar, not three stray controls: grouped on a card, equal
          widths on phones (stacked), a single row from sm up. */}
      <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          useFlexGap
          flexWrap="wrap"
        >
          <TextField
            size="small"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('catalog.search')}
            sx={{ flex: { sm: '1 1 240px' }, maxWidth: { sm: 420 } }}
            slotProps={{
              htmlInput: { 'aria-label': t('catalog.search') },
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRounded fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
          <TextField
            select
            size="small"
            value={department}
            onChange={(e) => setDepartment(e.target.value)}
            sx={{ width: { xs: '100%', sm: 200 } }}
            slotProps={{
              select: {
                displayEmpty: true,
                SelectDisplayProps: { 'aria-label': t('catalog.allDepartments') },
              },
            }}
          >
            <MenuItem value="">{t('catalog.allDepartments')}</MenuItem>
            {departments.map((code) => (
              <MenuItem key={code} value={code}>
                {code}
              </MenuItem>
            ))}
          </TextField>
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            sx={{
              flexWrap: { xs: 'nowrap', sm: 'wrap' },
              overflowX: 'auto',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
              py: 0.25,
            }}
          >
            {(
              [
                ['', t('catalog.kindAll')],
                ['FORM', t('catalog.kindForm')],
                ['JOURNEY', t('catalog.kindJourney')],
              ] as const
            ).map(([value, label]) => (
              <Chip
                key={value}
                label={label}
                onClick={() => setKind(value)}
                color={kind === value ? 'primary' : 'default'}
                variant={kind === value ? 'filled' : 'outlined'}
                sx={{ minHeight: 32, flexShrink: 0 }}
              />
            ))}
          </Stack>
        </Stack>
      </Paper>

      {isError ? <Alert severity="error">{t('common.error')}</Alert> : null}

      {!isLoading && filtered.length === 0 && !isError ? (
        <Paper variant="outlined" sx={{ p: 5, textAlign: 'center' }}>
          <Box
            aria-hidden
            sx={{
              width: 60,
              height: 60,
              borderRadius: 3,
              display: 'grid',
              placeItems: 'center',
              mx: 'auto',
              mb: 1.5,
              color: 'var(--fms-color-warning)',
              bgcolor: 'background.default',
            }}
          >
            <SearchOffRounded />
          </Box>
          <Typography sx={{ fontWeight: 700 }}>{t('catalog.empty')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
            {t('catalog.emptyHint')}
          </Typography>
          <Button variant="outlined" size="small" onClick={clearFilters}>
            {t('catalog.clear')}
          </Button>
        </Paper>
      ) : null}

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            sm: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(3, minmax(0, 1fr))',
          },
        }}
      >
        {isLoading
          ? Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={210} />
            ))
          : filtered.map((service) => {
              const isJourney = service.kind === 'JOURNEY';
              const live = liveByService.get(service.code);
              return (
                <Card
                  key={service.code}
                  variant="outlined"
                  sx={{ display: 'flex', flexDirection: 'column' }}
                >
                  <CardContent sx={{ flex: 1 }}>
                    <Stack direction="row" alignItems="flex-start" sx={{ mb: 1.5 }}>
                      <Box
                        aria-hidden
                        sx={{
                          width: 42,
                          height: 42,
                          borderRadius: 2.5,
                          display: 'grid',
                          placeItems: 'center',
                          color: tone(service.code),
                          bgcolor: 'background.default',
                        }}
                      >
                        {isJourney ? <RouteRounded /> : <DescriptionRounded />}
                      </Box>
                      <Box sx={{ flex: 1 }} />
                      <Chip
                        size="small"
                        label={isJourney ? t('catalog.badgeJourney') : t('catalog.badgeForm')}
                        color={isJourney ? 'secondary' : 'primary'}
                        variant="outlined"
                      />
                    </Stack>
                    <Typography variant="h3" component="h2" gutterBottom>
                      {resolveI18n(service.nameI18n, i18n.language) || service.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {resolveI18n(service.summaryI18n, i18n.language) || service.summary}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                      <Chip size="small" label={service.departmentCode} />
                    </Stack>
                  </CardContent>
                  <CardActions sx={{ px: 2, pb: 2 }}>
                    {live ? (
                      <Button
                        component={RouterLink}
                        to={`/applications/${live.id}`}
                        variant="outlined"
                        fullWidth
                      >
                        {t('catalog.trackExisting', { arn: live.arn })}
                      </Button>
                    ) : (
                      <Button
                        component={RouterLink}
                        to={`/apply/${service.code}`}
                        variant="contained"
                        fullWidth
                      >
                        {t('catalog.apply')}
                      </Button>
                    )}
                  </CardActions>
                </Card>
              );
            })}
      </Box>
    </Stack>
  );
}
